import React, { useEffect, useState, useCallback } from "react";
import { Database, ShieldAlert, FileClock, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELD_LABELS = {
  business_name: "Business Name", dba: "DBA", ein: "EIN", entity_type: "Entity Type",
  operating_states: "Operating States", registered_agent: "Registered Agent", owners: "Owners",
  business_address: "Business Address", mailing_address: "Mailing Address",
  insurance_policy_number: "Insurance Policy #", license_number: "License #",
  permit_number: "Permit #", tax_id: "Tax ID", issuing_agency: "Government Agency",
  renewal_date: "Renewal Date",
};

const ACTIONS = [
  { key: "update", label: "Update Memory" },
  { key: "keep", label: "Keep Existing" },
  { key: "save_both", label: "Save Both (Pending)" },
  { key: "remind", label: "Remind Later" },
];

export default function BusinessMemory({ onChange }) {
  const [mem, setMem] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/memory"); setMem(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    try {
      await api.post(`/memory/conflicts/${id}/resolve`, { action });
      toast.success(action === "remind" ? "Dox will remind you later." : "Business Memory updated.");
      await load();
      onChange && onChange();
    } catch { toast.error("Could not resolve."); }
  };

  if (!mem) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  const fields = Object.entries(mem.fields || {}).filter(([, v]) => v && v.value && (!Array.isArray(v.value) || v.value.length));
  const conflicts = mem.pending_conflicts || [];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-surface to-elevated p-6 relative overflow-hidden">
        <div className="glow-orb w-64 h-64 -right-16 -top-16 bg-dox-purple/15" />
        <div className="relative flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-dox-purple/15 text-dox-purple flex items-center justify-center"><Database size={24} /></div>
          <div>
            <h2 className="font-display font-black text-2xl">Business Memory</h2>
            <p className="text-white/55 text-sm mt-0.5">Everything Dox knows about your business — it gets smarter with every document.</p>
          </div>
        </div>
      </div>

      {conflicts.length > 0 && (
        <div className="space-y-3" data-testid="verification-mode">
          <div className="flex items-center gap-2 text-amber-400 font-display font-bold">
            <ShieldAlert size={18} /> Verification needed ({conflicts.length})
          </div>
          {conflicts.map((c) => (
            <div key={c.id} data-testid={`conflict-${c.id}`} className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-5">
              <div className="text-sm text-white/70 mb-3">
                Dox found a different <span className="font-semibold text-white">{c.field_label}</span> in
                <span className="text-white/80"> {c.document}</span>. Nothing was overwritten — you decide:
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Current</div>
                  <div className="text-sm text-white mt-1">{fmt(c.current)}</div>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">From new document</div>
                  <div className="text-sm text-white mt-1">{fmt(c.incoming)}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {ACTIONS.map((a) => (
                  <button
                    key={a.key}
                    data-testid={`resolve-${a.key}-${c.id}`}
                    onClick={() => resolve(c.id, a.key)}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                      a.key === "update" ? "bg-dox-orange text-white hover:bg-dox-orangeHover" : "glass text-white/80 hover:bg-white/10"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="glass-card p-6">
        <h3 className="font-display font-bold text-lg mb-4">What Dox remembers</h3>
        {fields.length === 0 ? (
          <p className="text-white/40 text-sm">Nothing yet. Give Dox documents and it will start building your memory.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {fields.map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-3 border-b border-white/5 pb-3">
                <span className="text-white/50 text-sm">{FIELD_LABELS[k] || k}</span>
                <span className="text-white text-sm text-right font-medium" data-testid={`memory-${k}`}>
                  {fmt(v.value)}
                  {v.status === "pending_review" && v.alternates?.length > 0 && (
                    <span className="block text-xs text-amber-400 mt-0.5">also on file: {v.alternates.join(", ")}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <MemList icon={FileClock} title="Filing history" items={(mem.filing_history || []).map((f) => `${f.document_type}${f.jurisdiction ? ` · ${f.jurisdiction}` : ""}`)} />
        <MemList icon={Layers} title="Documents on file" items={(mem.uploaded_documents || []).map((u) => `${u.filename} · ${u.category}`)} />
      </div>
    </div>
  );
}

function fmt(v) { return Array.isArray(v) ? v.join(", ") : String(v ?? ""); }

function MemList({ icon: Icon, title, items }) {
  return (
    <div className="glass-card p-5">
      <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Icon size={16} className="text-dox-orange" /> {title}</h3>
      {items.length === 0 ? <p className="text-white/40 text-sm">None yet.</p> : (
        <ul className="space-y-2">
          {items.slice(0, 8).map((t, i) => <li key={i} className="text-sm text-white/65 truncate">{t}</li>)}
        </ul>
      )}
    </div>
  );
}
